import {
  all,
  call,
  cancel,
  delay,
  fork,
  put,
  select,
  takeLatest,
} from 'redux-saga/effects';
import moment from 'moment';

import { downloadDocumentSaga } from '../../utils/downloadDocument';
import AsyncStorage from '../../utils/AsyncStorage';
import { get } from '../../utils/request';
import MobileGlobalHelpers from '../../utils/MobileGlobalHelpers';

import { getCookie, setCookie } from '../../utils/globalHelpers';
import makeSelectMain from '../Main/selectors';
import makeSelectMobile from '../Mobile/selectors';
import {
  downloadDocument,
  downloadDocumentFailure,
  fetchDocumentsHomeFailure,
  fetchDocumentsHomeSuccess,
  fetchStatementFailure,
  fetchStatementSuccess,
  fetchStatements,
  fetchStatementsFailure,
  fetchStatementsSuccess,
  refreshStatementsUrl,
  setHomeLoadingStep,
  setStatementsFile,
} from './actions';
import {
  DOWNLOAD_DOCUMENT,
  FETCH_DOCUMENTS_HOME,
  FETCH_STATEMENT,
  FETCH_STATEMENTS,
  REFRESH_STATEMENTS_URL,
} from './constants';
import { selectServiceErrorMessage } from './selectors';

export function* fetchDocumentsHomeSaga({ baseUrl }) {
  try {
    const loanNumber = yield select(makeSelectMain('selectedLoan'));
    const endpoint = `${baseUrl}/documents/home?loanNumber=${loanNumber}`;
    const loadingSaga = yield fork(fetchDocumentsHomeLoadingSaga);
    const { numPayoff, numStatements } = yield call(get, endpoint);
    yield cancel(loadingSaga);
    yield put(fetchDocumentsHomeSuccess({ numPayoff, numStatements }));
  } catch {
    const serviceErrorMessage = yield select(selectServiceErrorMessage());
    yield put(fetchDocumentsHomeFailure(serviceErrorMessage));
  }
}

export function* fetchDocumentsHomeLoadingSaga() {
  yield delay(1500);
  yield put(setHomeLoadingStep(2));
  yield delay(1500);
  yield put(setHomeLoadingStep(3));
  yield delay(1500);
  yield put(setHomeLoadingStep(4));
  yield delay(1500);
  yield put(setHomeLoadingStep(5));
}

// eslint-disable-next-line no-unused-vars
export function* fetchStatementSaga({ baseUrl }, { payload }) {
  const {
    statementId,
    statementUrl,
  } = payload;
  try {
    const fileName = `statement_${statementId}.pdf`;
    const isMobile = yield select(makeSelectMobile('isMobile'));
    const isNativeMobile = yield select(makeSelectMobile('isNativeMobile'));
    if (isNativeMobile) {
      yield MobileGlobalHelpers.saveUrlToFile({ fileName, url: statementUrl });
    } else if (!isMobile) {
      yield put(downloadDocument({ fileUrl: statementUrl, modifiedFileName: fileName }));
    }
    yield put(setStatementsFile({ fileName, uri: statementUrl }));
    yield put(fetchStatementSuccess());
  } catch (err) {
    const serviceErrorMessage = yield select(selectServiceErrorMessage());
    yield put(fetchStatementFailure(serviceErrorMessage));
  }
}

export function* fetchStatementsSaga({ baseUrl }) {
  try {
    const isNativeMobile = yield select(makeSelectMobile('isNativeMobile'));
    const loanNumber = yield select(makeSelectMain('selectedLoan'));
    const endpoint = `${baseUrl}/documents/statements/?loanNumber=${loanNumber}`;
    const { statements } = yield call(get, endpoint);
    const { statementUrlExpiresAt } = statements[0];
    if (isNativeMobile) {
      yield call(AsyncStorage.setItem, 'statementUrlExpiresAt', statementUrlExpiresAt);
    } else {
      setCookie('statementUrlExpiresAt', statementUrlExpiresAt);
    }
    yield put(fetchStatementsSuccess(statements));
    yield put(refreshStatementsUrl());
  } catch {
    const serviceErrorMessage = yield select(selectServiceErrorMessage());
    yield put(fetchStatementsFailure(serviceErrorMessage));
  }
}

export function* refreshStatementsUrlSaga() {
  try {
    let delayTime = 300000; // 5 min in ms
    const isNativeMobile = yield select(makeSelectMobile('isNativeMobile'));
    const isLoggedIn = yield select(makeSelectMain('isLoggedIn'));
    const statementUrlExpiresAt = isNativeMobile
      ? yield call(AsyncStorage.getItem, 'statementUrlExpiresAt')
      : getCookie('statementUrlExpiresAt');
    while (isLoggedIn) {
      yield delay(delayTime);
      const isStillLoggedIn = yield select(makeSelectMain('isLoggedIn'));
      if (!isStillLoggedIn) {
        break;
      }
      const expirationTime = moment(statementUrlExpiresAt).diff(moment());
      // Set delay time to 5 minutes, or less if URL is about to expire
      delayTime = expirationTime > delayTime ? delayTime : expirationTime;
      if (delayTime <= 0) {
        yield put(fetchStatements());
        delayTime = 300000; // 5 min in ms
      }
    }
  } catch {
    const serviceErrorMessage = yield select(selectServiceErrorMessage());
    yield put(fetchStatementFailure(serviceErrorMessage));
  }
}

export default (options) => function* watcherSaga() {
  yield all([
    takeLatest(
      DOWNLOAD_DOCUMENT,
      downloadDocumentSaga,
      {
        ...options,
        downloadDocumentFailure,
      },
    ),
    takeLatest(FETCH_DOCUMENTS_HOME, fetchDocumentsHomeSaga, options),
    takeLatest(FETCH_STATEMENT, fetchStatementSaga, options),
    takeLatest(FETCH_STATEMENTS, fetchStatementsSaga, options),
    takeLatest(REFRESH_STATEMENTS_URL, refreshStatementsUrlSaga, options),
  ]);
};
